import { useEffect } from 'react';

import { useNavigate } from 'react-router';

import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '@/store/store';
import { fetchSearchData, setSearchItem } from '@/store/slices/searchSlice';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';

import { ChevronUpIcon } from '@heroicons/react/20/solid';

import { motion } from 'framer-motion';
import { formatDate } from '@/helpers/formatDate';
import { formatNumber } from '@/helpers/formatNumber';

//const getPrefix = (key: string) => {
//  switch (key) {
//    case 'claims':
//      return 'Claim';
//    case 'startet':
//      return 'Startet';
//    case 'cases':
//      return 'Case';
//    default:
//      return key;
//  }
//};

const filterData = <
  T extends {
    debtor?: { name?: string; partner_id?: string | number };
    claims?: Array<{ claim_number?: string }>;
    dueDate?: string; // example "2025-04-18"
    amountDue?: number | string;
  }
>(
  data: T[],
  query: string
): T[] => {
  const q = query.trim().toLowerCase();

  const isNumber = !isNaN(Number(q));
  const isDateFormat = /^\d{4}-\d{2}-\d{2}$/.test(q);

  // tolerance for floating point numbers
  const EPSILON = 0.01;

  return data.filter((item) => {
    const nameMatch = item.debtor?.name
      ? item.debtor.name.toLowerCase().includes(q)
      : false;

    // const claimsMatch = item.claims
    //   ? item.claims.some(
    //     (claim) =>
    //       claim.claim_number && claim.claim_number.toLowerCase().includes(q)
    //   )
    //   : false;

    const amountMatch =
      item.amountDue !== undefined
        ? Math.abs(Number(item.amountDue) - Number(q)) < EPSILON
        : false;

    const dateMatch = item.dueDate ? item.dueDate.includes(q) : false;

    if (isNumber) {
      return amountMatch;
    } else if (isDateFormat) {
      return dateMatch;
    } else {
      return nameMatch /*|| claimsMatch*/;
    }
  });
};

type BaseItem = {
  id: number;
  debtor?: { name?: string; partner_id?: string | number };
  dueDate?: string;
  amountDue?: number | string;
};

type KravItem = BaseItem & {
  isMissingInformation?: boolean;
};

type StartetItem = BaseItem & {
  lastAction?: { date?: string };
  nextAction?: { date?: string };
};

type SagerItem = BaseItem & {
  isOpen?: boolean;
};

type SearchItem = KravItem | StartetItem | SagerItem;

/**
 * Search component that fetches and displays filtered search results.
 *
 * The component retrieves data for claims, startet, and cases from the Redux store,
 * filters the data based on the search value stored in the state, and displays the
 * results using Disclosure components with animated list items via Framer Motion.
 * Clicking on an item clears the search value and redirects to the corresponding route.
 */

const Search = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { krav, startet, sager, searchValue } = useSelector(
    (state: RootState) => state.search
  );

  useEffect(() => {
    dispatch(fetchSearchData());
  }, [dispatch]);

  const results = {
    krav: { ...krav, data: filterData(krav.data, searchValue) },
    startet: { ...startet, data: filterData(startet.data, searchValue) },
    sager: { ...sager, data: filterData(sager.data, searchValue) },
  };

  const handleRedirect = (key: string, item: unknown) => {
    dispatch(setSearchItem({ key, data: [item] }));

    navigate(`/focus`);
  };

  const formatViewData = (key: string, item: SearchItem) => {
    switch (key) {
      case 'krav': {
        const kravItem = item as KravItem;
        return (
          <>
            {kravItem?.debtor?.name} {' | '}
            {formatDate(kravItem?.dueDate)} {' | '}
            {formatNumber(kravItem?.amountDue)} kr
            {kravItem.isMissingInformation !== undefined && (
              <>
                {' | '}
                {kravItem.isMissingInformation
                  ? 'MANGLER INFORMATION'
                  : 'START RYKKERPROCES'}
              </>
            )}
          </>
        );
      }

      case 'startet': {
        const startetItem = item as StartetItem;
        return (
          <>
            {startetItem?.debtor?.name} {' | '}
            Seneste handling: {formatDate(startetItem?.lastAction?.date)}{' '}
            {' | '}
            Næste handling: {formatDate(startetItem?.nextAction?.date)}
          </>
        );
      }

      case 'sager': {
        const sagerItem = item as SagerItem;
        return (
          <>
            {sagerItem?.debtor?.name} {' | '}
            {formatDate(sagerItem?.dueDate)} {' | '}
            {formatNumber(sagerItem?.amountDue)} kr
            {sagerItem.isOpen !== undefined && (
              <>
                {' | '}
                {sagerItem.isOpen ? 'ÅBEN' : 'AFSLUTTET'}
              </>
            )}
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="p-4 flex flex-col gap-3 max-w-[1120px] mx-auto">
      {Object.entries(results).map(([key, value]) => (
        <Disclosure key={key}>
          {({ open }) => (
            <>
              <DisclosureButton
                className="flex justify-between w-full p-4 font-jostMedium font-medium
                           text-left text-white bg-[#1F2B40] hover:bg-[#2a3a55]
                           focus:outline-none focus-visible:ring focus-visible:ring-blue-500
                           focus-visible:ring-opacity-75 rounded-md">
                <span>
                  {key.toUpperCase()} ({value.data.length})
                </span>
                <ChevronUpIcon
                  className={`${
                    open ? 'transform rotate-180' : ''
                  } w-5 h-5 text-white`}
                />
              </DisclosureButton>

              <DisclosurePanel className="px-4 pt-4 pb-4 text-sm rounded-md">
                {value.data.length === 0 ? (
                  <p className="font-jostMedium text-xl text-[rgba(31,43,64,0.8)]">
                    Ingen resultater fundet for {key}
                  </p>
                ) : (
                  <motion.ul
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.1,
                        },
                      },
                    }}
                    initial="hidden"
                    animate="visible"
                    className="space-y-2 list-none p-0 m-0">
                    {value.data.map((item) => (
                      <motion.li
                        key={item.id}
                        variants={{
                          hidden: { opacity: 0, y: 10 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        onClick={() => handleRedirect(key, item)}
                        transition={{ duration: 0.3 }}
                        className="flex items-center cursor-pointer bg-[linear-gradient(108.46deg,rgba(255,255,255,0.24)_0%,rgba(255,255,255,0.06)_100%)] shadow-[0px_5px_5.8px_-2px_rgba(31,43,64,0.06)] py-3 px-4 text-lg font-jostMedium font-medium leading-[18px] text-[rgba(31,43,64,0.8)] rounded-md h-[60px] hover:scale-[1.02] transition-transform duration-300 ease-in-out border border-gray-400">
                        {formatViewData(key, item)}
                      </motion.li>
                    ))}
                  </motion.ul>
                )}
              </DisclosurePanel>
            </>
          )}
        </Disclosure>
      ))}
    </div>
  );
};

export default Search;
